// Persistent, TTL'd, budget-guarded cache for the weather grid.
//
// Open-Meteo's free tier is rate-limited by a WEIGHTED count, not a raw request count:
//   weight = nLocations · (nDays / 14) · (nVariables / 10)
// so one ~100-point grid fetch costs several units of the 10 000/day budget — heavy map panning can
// exhaust it fast. These helpers keep us far under it, together with lattice-snapped cache keys at the
// call site (nearby views share one entry):
//   • a localStorage cache that survives reloads (TTL'd — planning-scale weather is stable for minutes),
//   • a self-imposed daily WEIGHT budget that soft-pauses fetching well before the hard limit, protecting
//     the per-IP quota shared by every tab, the dev server, and anyone else behind the same NAT.

const CACHE_KEY = 'dp.wx.cache.v1';
const BUDGET_KEY = 'dp.wx.budget.v1';
const TTL_MS = 20 * 60 * 1000; // 20 min — a forecast hour is stable well past this; current obs drift slowly
const MAX_ENTRIES = 24; // LRU cap (~a few hundred KB of grids)

/** Self-imposed daily cap, well under Open-Meteo's 10 000/day so the shared IP keeps ample headroom. */
export const WX_DAILY_WEIGHT_CAP = 3000;

interface Entry<T = unknown> {
  ts: number;
  payload: T;
}
type Store = Record<string, Entry>;

function readStore(): Store {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as Store) : {};
  } catch {
    return {}; // corrupt JSON / no localStorage — the cache is a nicety, degrade to no-cache
  }
}
function writeStore(s: Store): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(s));
  } catch {
    // quota exceeded / private mode — silently skip; the in-memory grid still works this session
  }
}

/** Fresh cached payload for `key`, or null when absent or past its TTL. */
export function wxCacheGet<T>(key: string): T | null {
  const e = readStore()[key];
  if (!e || Date.now() - e.ts > TTL_MS) return null;
  return e.payload as T;
}

/** Store `payload` under `key`, evicting the oldest entries once past MAX_ENTRIES (LRU by write time). */
export function wxCacheSet<T>(key: string, payload: T): void {
  const s = readStore();
  s[key] = { ts: Date.now(), payload };
  const keys = Object.keys(s);
  if (keys.length > MAX_ENTRIES) {
    keys.sort((a, b) => s[a].ts - s[b].ts); // oldest first
    for (const k of keys.slice(0, keys.length - MAX_ENTRIES)) delete s[k];
  }
  writeStore(s);
}

function utcDay(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}-${d.getUTCDate()}`;
}

/** Approximate weight spent today (UTC). Rolls over automatically at UTC midnight. */
export function wxBudgetSpent(): number {
  try {
    const raw = localStorage.getItem(BUDGET_KEY);
    if (!raw) return 0;
    const b = JSON.parse(raw) as { day: string; weight: number };
    return b.day === utcDay() ? b.weight : 0;
  } catch {
    return 0;
  }
}

/** Add `weight` to today's tally (resetting first if the UTC day rolled over). */
export function wxBudgetAdd(weight: number): void {
  try {
    localStorage.setItem(BUDGET_KEY, JSON.stringify({ day: utcDay(), weight: wxBudgetSpent() + weight }));
  } catch {
    // ignore — worst case we under-count and rely on the server's own 429 backoff
  }
}
